/**
 * useApi Custom Hook
 * Handles API requests with loading, error, and data state management.
 * 
 * Main logic:
 * - Accepts an async function for API call.
 * - Manages loading, error, and response data state.
 * - Returns state and a trigger function.
 * 
 * All variable names, logs, and comments are in English.
 */

import { useState } from "react";

type ApiState<T> = {
  loading: boolean;
  error: string | null;
  data: T | null;
};

export function useApi<TResult, TArgs = void>(apiFunc: (args: TArgs) => Promise<TResult>) {
  const [state, setState] = useState<ApiState<TResult>>({
    loading: false,
    error: null,
    data: null,
  });

  const trigger = async (args: TArgs): Promise<TResult> => {
    setState({ loading: true, error: null, data: null });
    try {
      const data = await apiFunc(args);
      setState({ loading: false, error: null, data });
      return data;
    } catch (error: unknown) {
      const errMsg = error && typeof error === "object" && "message" in error
        ? String((error as { message?: unknown }).message)
        : "API error";
      setState({ loading: false, error: errMsg, data: null });
      throw error;
    }
  };

  return { ...state, trigger };
}
